import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import Portal from '../../components/Portal';
import {
  UserGroupIcon,
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  BanknotesIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';

const api = (path) => `${process.env.REACT_APP_API_URL}${path}`;

function formatTransactionDescription(description) {
  return (description || '').replace(/^Dette miellerie/, 'Dette usage de la miellerie');
}

function typeLabel(type) {
  const m = {
    paiement: 'Paiement',
    reservation: 'Dette',
    annulation: 'Annulation',
    ajustement: 'Ajustement',
  };
  return m[type] || type;
}

function paymentMethodLabel(methode) {
  const m = {
    espece: 'Espèces',
    cheque: 'Chèque',
    stripe: 'Carte',
    virement: 'Virement',
    solde: 'Avoir',
  };
  return m[methode] || methode;
}

function slugNomFichier(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .toLowerCase();
}

function exportHistoriqueResponsableExcel(data, annee) {
  const resp = data?.responsable?.user;
  const nomResp = [resp?.prenom, resp?.nom].filter(Boolean).join(' ') || 'responsable';
  const transactions = data?.transactions || [];

  const rows = transactions.map((t) => {
    const usager = t.user;
    const created = t.createdAt ? new Date(t.createdAt) : null;
    return {
      Date: created ? created.toLocaleDateString('fr-FR') : '',
      Heure: created
        ? created.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
        : '',
      Type: typeLabel(t.type),
      Intitulé: formatTransactionDescription(t.description),
      Usager: usager ? `${usager.prenom || ''} ${usager.nom || ''}`.trim() : '',
      'Email usager': usager?.email || '',
      Méthode: t.paiement?.methode ? paymentMethodLabel(t.paiement.methode) : '',
      'N° chèque / réf.': t.paiement?.reference || '',
      'Montant (€)': Number(t.montant || 0),
      'Validé par': t.effectuePar
        ? `${t.effectuePar.prenom || ''} ${t.effectuePar.nom || ''}`.trim()
        : '',
      'Responsable du créneau': nomResp,
    };
  });

  const totaux = data?.totaux || {};
  const recap = [
    { Indicateur: 'Responsable', Valeur: nomResp },
    { Indicateur: 'Année', Valeur: annee },
    { Indicateur: 'Nombre de transactions', Valeur: totaux.nombreTransactions ?? rows.length },
    { Indicateur: 'Encaissements (€)', Valeur: Number(totaux.totalEncaissements || 0) },
    { Indicateur: 'Dettes (€)', Valeur: Number(totaux.totalDettes || 0) },
    { Indicateur: 'Total (€)', Valeur: Number(totaux.total || 0) },
  ];

  const workbook = XLSX.utils.book_new();
  const wsTx = XLSX.utils.json_to_sheet(rows);
  wsTx['!cols'] = [
    { wch: 12 },
    { wch: 8 },
    { wch: 14 },
    { wch: 48 },
    { wch: 22 },
    { wch: 28 },
    { wch: 12 },
    { wch: 18 },
    { wch: 12 },
    { wch: 22 },
    { wch: 24 },
  ];
  XLSX.utils.book_append_sheet(workbook, wsTx, 'Transactions');

  const wsRecap = XLSX.utils.json_to_sheet(recap);
  wsRecap['!cols'] = [{ wch: 28 }, { wch: 24 }];
  XLSX.utils.book_append_sheet(workbook, wsRecap, 'Récapitulatif');

  const fileName = `miellerie_paiements_${slugNomFichier(nomResp)}_${annee}.xlsx`;
  XLSX.writeFile(workbook, fileName);
}

export default function GestionResponsables({
  canManageResponsables = false,
  canExportStats = false,
}) {
  const currentYear = new Date().getFullYear();
  const [actifs, setActifs] = useState([]);
  const [historiques, setHistoriques] = useState([]);
  const [usersActifsIds, setUsersActifsIds] = useState([]);
  const [utilisateursEligibles, setUtilisateursEligibles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAdherent, setSelectedAdherent] = useState(null);
  const [annee, setAnnee] = useState(currentYear);
  const [error, setError] = useState('');
  const [renommerLoadingId, setRenommerLoadingId] = useState(null);
  const [historiqueMandatsOpen, setHistoriqueMandatsOpen] = useState(false);

  const [historiqueTarget, setHistoriqueTarget] = useState(null);
  const [historiqueAnnee, setHistoriqueAnnee] = useState(currentYear);
  const [historiqueLoading, setHistoriqueLoading] = useState(false);
  const [historiqueData, setHistoriqueData] = useState(null);
  const [historiqueError, setHistoriqueError] = useState('');

  useEffect(() => {
    fetchResponsables();
    if (canManageResponsables) {
      fetchUtilisateursEligibles();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [annee, canManageResponsables]);

  useEffect(() => {
    if (!historiqueTarget) return;
    fetchHistorique(historiqueTarget._id, historiqueAnnee);
  }, [historiqueTarget, historiqueAnnee]);

  const fetchResponsables = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        api(`/miellerie/responsables?annee=${annee}&inclureInactifs=1`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActifs(Array.isArray(data?.actifs) ? data.actifs : []);
      setHistoriques(Array.isArray(data?.historiques) ? data.historiques : []);
      setUsersActifsIds(Array.isArray(data?.usersActifsIds) ? data.usersActifsIds : []);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des responsables');
      setLoading(false);
    }
  };

  const fetchUtilisateursEligibles = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(api(`/miellerie/responsables/eligibles?annee=${annee}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUtilisateursEligibles(data);
    } catch (err) {
      console.error('Erreur chargement utilisateurs éligibles:', err);
    }
  };

  const fetchHistorique = async (responsableId, year) => {
    setHistoriqueLoading(true);
    setHistoriqueError('');
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(
        api(`/miellerie/responsables/${responsableId}/transactions?annee=${year}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistoriqueData(data);
    } catch (err) {
      setHistoriqueData(null);
      setHistoriqueError(
        err.response?.data?.message || "Erreur lors du chargement de l'historique"
      );
    } finally {
      setHistoriqueLoading(false);
    }
  };

  const openHistorique = (responsable) => {
    setHistoriqueAnnee(annee);
    setHistoriqueData(null);
    setHistoriqueError('');
    setHistoriqueTarget(responsable);
  };

  const closeHistorique = () => {
    setHistoriqueTarget(null);
    setHistoriqueData(null);
    setHistoriqueError('');
  };

  const handleExportHistoriqueExcel = () => {
    if (!historiqueData?.transactions?.length) {
      toast.error("Aucune transaction à exporter pour cette année.");
      return;
    }
    try {
      exportHistoriqueResponsableExcel(historiqueData, historiqueAnnee);
      toast.success('Export Excel téléchargé');
    } catch (err) {
      console.error(err);
      toast.error("Impossible de générer l'export Excel");
    }
  };

  const filteredEligibles = useMemo(() => {
    if (!searchTerm.trim()) return [];
    const q = searchTerm.toLowerCase();
    return utilisateursEligibles
      .filter(
        (u) =>
          u.nom?.toLowerCase().includes(q) ||
          u.prenom?.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q)
      )
      .slice(0, 20);
  }, [searchTerm, utilisateursEligibles]);

  const closeAddModal = () => {
    setShowAddModal(false);
    setSearchTerm('');
    setSelectedAdherent(null);
    setError('');
  };

  const handleNommerResponsable = async (e) => {
    e.preventDefault();
    if (!selectedAdherent?._id) {
      setError('Veuillez sélectionner un utilisateur');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        api('/miellerie/responsables'),
        { userId: selectedAdherent._id, annee },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Responsable nommé avec succès. Un email de notification a été envoyé.');
      closeAddModal();
      fetchResponsables();
      fetchUtilisateursEligibles();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la nomination');
    }
  };

  const handleRetirerResponsable = async (responsableId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir retirer ce responsable ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(api(`/miellerie/responsables/${responsableId}`), {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Responsable retiré — conservé dans l’historique');
      fetchResponsables();
      fetchUtilisateursEligibles();
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du retrait');
    }
  };

  const handleRenommer = async (responsable) => {
    const userId = responsable.user?._id;
    if (!userId) return;
    if (!responsable.adhesionActive) {
      toast.error('Impossible de renommer : adhésion AMAIR inactive pour cette année.');
      return;
    }
    if (usersActifsIds.includes(String(userId))) {
      toast.error('Cette personne est déjà responsable actif pour cette année.');
      return;
    }
    if (!window.confirm(`Renommer ${responsable.user.prenom} ${responsable.user.nom} responsable pour ${annee} ?`)) {
      return;
    }

    setRenommerLoadingId(responsable._id);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        api('/miellerie/responsables'),
        { userId, annee },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Responsable renommé. Un email de notification a été envoyé.');
      fetchResponsables();
      fetchUtilisateursEligibles();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erreur lors de la re-nomination');
    } finally {
      setRenommerLoadingId(null);
    }
  };

  const anneesOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear + 1; y >= currentYear - 5; y -= 1) years.push(y);
    return years;
  }, [currentYear]);

  const renderActifsTable = () => {
    if (actifs.length === 0) {
      return (
        <div className="p-10 text-center">
          <UserGroupIcon className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">Aucun responsable actif pour l&apos;année {annee}</p>
          <p className="text-slate-400 text-sm mt-1">
            {canManageResponsables
              ? 'Commencez par nommer un responsable'
              : 'Aucun responsable nommé pour cette année'}
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 shadow">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Responsable
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                Téléphone
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                Adhésion
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                Nommé le
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {actifs.map((responsable) => (
              <tr key={responsable._id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 h-10 w-10 bg-honey-100 rounded-full flex items-center justify-center">
                      <span className="text-honey-700 font-semibold text-sm">
                        {responsable.user.prenom?.[0]}
                        {responsable.user.nom?.[0]}
                      </span>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">
                        {responsable.user.prenom} {responsable.user.nom}
                      </div>
                      {responsable.derogationAcces && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-honey-100 text-honey-800 mt-0.5">
                          Dérogation
                        </span>
                      )}
                      {responsable.notificationEnvoyee && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700 mt-0.5 ml-1">
                          Email envoyé
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                  <div className="text-sm text-gray-900">{responsable.user.email}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                  <div className="text-sm text-gray-900">
                    {responsable.user.telephone || <span className="text-gray-400">—</span>}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center hidden sm:table-cell">
                  {responsable.adhesionActive ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircleIcon className="h-3.5 w-3.5 mr-1" />
                      Active
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-honey-100 text-honey-800">
                      <ExclamationTriangleIcon className="h-3.5 w-3.5 mr-1" />
                      Inactive
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 hidden sm:table-cell">
                  {new Date(responsable.dateNomination).toLocaleDateString('fr-FR')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right">
                  <div className="inline-flex items-center gap-1.5">
                    <button
                      type="button"
                      onClick={() => openHistorique(responsable)}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 rounded-md text-slate-700 bg-white hover:bg-slate-50 text-xs font-semibold transition-colors"
                      title="Historique des transactions"
                    >
                      <ClipboardDocumentListIcon className="h-4 w-4" />
                      Historique
                    </button>
                    {canManageResponsables && (
                    <button
                      onClick={() => handleRetirerResponsable(responsable._id)}
                      className="inline-flex items-center p-1.5 border border-red-200 rounded-md text-red-600 bg-white hover:bg-red-50 transition-colors"
                      title="Retirer ce responsable"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-honey-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <XCircleIcon className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Erreur</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
            ×
          </button>
        </div>
      )}

      <div>
        <div className="pb-3 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-slate-900">
            Responsables actifs ({actifs.length})
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <label className="inline-flex items-center gap-2 text-sm text-slate-600">
              <select
                value={annee}
                onChange={(e) => {
                  setAnnee(parseInt(e.target.value, 10));
                  setLoading(true);
                }}
                className="px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
              >
                {anneesOptions.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </label>
            {canManageResponsables && (
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex text-sm items-center px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700 transition-colors whitespace-nowrap"
            >
              <PlusIcon className="h-4 w-4 mr-2 mb-0.5" />
              Nommer un responsable
            </button>
            )}
          </div>
        </div>
        {renderActifsTable()}
      </div>

      <div>
        <button
          type="button"
          onClick={() => setHistoriqueMandatsOpen((o) => !o)}
          className="w-full pb-3 border-b border-slate-100 flex items-start justify-between gap-3 text-left group"
          aria-expanded={historiqueMandatsOpen}
        >
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-slate-900 group-hover:text-honey-800">
              Historique des mandats ({historiques.length})
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">
              Responsables retirés pour l&apos;année {annee} — conservés pour suivi et transactions
            </p>
          </div>
          <ChevronDownIcon
            className={`h-5 w-5 text-slate-400 shrink-0 mt-1 transition-transform ${
              historiqueMandatsOpen ? 'rotate-180' : ''
            }`}
            aria-hidden
          />
        </button>

        {historiqueMandatsOpen && (
          <>
            {historiques.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-sm text-slate-400">Aucun mandat retiré pour cette année</p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg mt-3">
                <table className="min-w-full divide-y divide-gray-200 shadow">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Responsable
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        Email
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                        Période
                      </th>
                      <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                        Adhésion
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {historiques.map((responsable) => {
                      const userId = String(responsable.user?._id || '');
                      const dejaActif = usersActifsIds.includes(userId);
                      const peutRenommer = responsable.adhesionActive && !dejaActif;
                      return (
                        <tr key={responsable._id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10 bg-slate-100 rounded-full flex items-center justify-center">
                                <span className="text-slate-600 font-semibold text-sm">
                                  {responsable.user.prenom?.[0]}
                                  {responsable.user.nom?.[0]}
                                </span>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {responsable.user.prenom} {responsable.user.nom}
                                </div>
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-600 mt-0.5">
                                  Mandat terminé
                                </span>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                            <div className="text-sm text-gray-900">{responsable.user.email}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500 hidden sm:table-cell">
                            {new Date(responsable.dateNomination).toLocaleDateString('fr-FR')}
                            {' → '}
                            {responsable.dateFin
                              ? new Date(responsable.dateFin).toLocaleDateString('fr-FR')
                              : '—'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center hidden lg:table-cell">
                            {responsable.adhesionActive ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-honey-100 text-honey-800">
                                Inactive
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            <div className="inline-flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => openHistorique(responsable)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-200 rounded-md text-slate-700 bg-white hover:bg-slate-50 text-xs font-semibold transition-colors"
                              >
                                <ClipboardDocumentListIcon className="h-4 w-4" />
                                Historique
                              </button>
                              {canManageResponsables && (
                              <button
                                type="button"
                                onClick={() => handleRenommer(responsable)}
                                disabled={!peutRenommer || renommerLoadingId === responsable._id}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-honey-200 rounded-md text-honey-800 bg-honey-50 hover:bg-honey-100 text-xs font-semibold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                title={
                                  dejaActif
                                    ? 'Déjà responsable actif'
                                    : !responsable.adhesionActive
                                      ? 'Adhésion inactive'
                                      : 'Renommer pour cette année'
                                }
                              >
                                <ArrowPathIcon className="h-4 w-4" />
                                {renommerLoadingId === responsable._id ? '…' : 'Renommer'}
                              </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {historiqueTarget && (
        <Portal>
        <div className="fixed top-0 left-0 z-[55] flex h-[100dvh] w-screen items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-slate-900">
                  Historique — {historiqueTarget.user?.prenom} {historiqueTarget.user?.nom}
                </h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  Transactions où cette personne était responsable du créneau
                </p>
              </div>
              <button
                type="button"
                onClick={closeHistorique}
                className="text-slate-400 hover:text-slate-600"
                aria-label="Fermer"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="px-6 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                <select
                  value={historiqueAnnee}
                  onChange={(e) => setHistoriqueAnnee(parseInt(e.target.value, 10))}
                  className="px-2.5 py-1.5 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-honey-500 focus:border-honey-500"
                >
                  {anneesOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </label>
              <div className="flex flex-wrap items-center gap-2">
              {historiqueData?.totaux && (
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-medium">
                    {historiqueData.totaux.nombreTransactions} transaction
                    {historiqueData.totaux.nombreTransactions !== 1 ? 's' : ''}
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-green-100 text-green-800 font-medium">
                    Encaissements {historiqueData.totaux.totalEncaissements.toFixed(2)} €
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-100 text-red-800 font-medium">
                    Dettes {historiqueData.totaux.totalDettes.toFixed(2)} €
                  </span>
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-honey-100 text-honey-900 font-semibold">
                    <BanknotesIcon className="h-3.5 w-3.5" />
                    Total {historiqueData.totaux.total.toFixed(2)} €
                  </span>
                </div>
              )}
              {canExportStats && (
                <button
                  type="button"
                  onClick={handleExportHistoriqueExcel}
                  disabled={historiqueLoading || !historiqueData?.transactions?.length}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg text-white bg-honey-600 hover:bg-honey-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  Extraire Excel
                </button>
              )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {historiqueLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-honey-600" />
                </div>
              ) : historiqueError ? (
                <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-800">
                  {historiqueError}
                </div>
              ) : !historiqueData?.transactions?.length ? (
                <div className="text-center py-12">
                  <ClipboardDocumentListIcon className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm text-slate-500">
                    Aucune transaction pour l&apos;année {historiqueAnnee}
                  </p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {historiqueData.transactions.map((t) => {
                    const slot = t.reservation?.slot;
                    const dateSlot = slot?.date
                      ? new Date(slot.date).toLocaleDateString('fr-FR')
                      : null;
                    return (
                      <div
                        key={t._id}
                        className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${
                                t.type === 'paiement'
                                  ? 'bg-green-100 text-green-800'
                                  : t.type === 'reservation'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-slate-100 text-slate-700'
                              }`}
                            >
                              {typeLabel(t.type)}
                            </span>
                            {t.paiement?.methode && (
                              <span className="text-xs bg-slate-100 text-slate-700 px-2 py-0.5 rounded">
                                {paymentMethodLabel(t.paiement.methode)}
                              </span>
                            )}
                            {t.paiement?.methode === 'cheque' && (
                              <span
                                className={`text-xs px-2 py-0.5 rounded ${
                                  t.paiement?.chequeStatut === 'encaisse'
                                    ? 'bg-green-100 text-green-800'
                                    : 'bg-amber-100 text-amber-900'
                                }`}
                              >
                                {t.paiement?.chequeStatut === 'encaisse' ? 'Encaissé' : 'Non encaissé'}
                              </span>
                            )}
                          </div>
                          <p className="mt-1 text-sm font-medium text-slate-900 truncate">
                            {formatTransactionDescription(t.description)}
                          </p>
                          <p className="mt-0.5 text-xs text-slate-500">
                            Usager :{' '}
                            {t.user
                              ? `${t.user.prenom || ''} ${t.user.nom || ''}`.trim()
                              : '—'}
                            {dateSlot
                              ? ` · Créneau ${dateSlot}${
                                  slot?.heureDebut ? ` (${slot.heureDebut}–${slot.heureFin})` : ''
                                }`
                              : ''}
                          </p>
                          {t.effectuePar && (
                            <p className="mt-0.5 text-xs text-slate-400">
                              Validé par {t.effectuePar.prenom} {t.effectuePar.nom} le{' '}
                              {new Date(t.createdAt).toLocaleDateString('fr-FR')}
                            </p>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div
                            className={`text-base font-semibold tabular-nums ${
                              t.montant >= 0 ? 'text-green-700' : 'text-red-700'
                            }`}
                          >
                            {t.montant > 0 ? '+' : ''}
                            {Number(t.montant || 0).toFixed(2)} €
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200">
              <button
                type="button"
                onClick={closeHistorique}
                className="w-full px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
        </Portal>
      )}

      {showAddModal && (
        <Portal>
        <div className="fixed top-0 left-0 z-[55] flex h-[100dvh] w-screen items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">
                Nommer un nouveau responsable
              </h3>
            </div>

            <form onSubmit={handleNommerResponsable} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Rechercher un adhérent *
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Nom ou prénom..."
                    disabled={utilisateursEligibles.length === 0}
                    className="block w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-honey-500 focus:border-honey-500 disabled:bg-slate-100 disabled:cursor-not-allowed"
                  />
                </div>

                {searchTerm && filteredEligibles.length > 0 && (
                  <div className="mt-2 border border-slate-200 rounded-lg max-h-60 overflow-y-auto">
                    {filteredEligibles.map((u) => (
                      <button
                        key={u._id}
                        type="button"
                        onClick={() => {
                          setSelectedAdherent(u);
                          setSearchTerm('');
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                      >
                        <div className="font-medium text-slate-900">
                          {u.prenom} {u.nom}
                        </div>
                        <div className="text-sm text-slate-500">{u.email}</div>
                      </button>
                    ))}
                  </div>
                )}

                {searchTerm && filteredEligibles.length === 0 && utilisateursEligibles.length > 0 && (
                  <p className="text-sm text-slate-500 mt-2">
                    Aucun adhérent ne correspond à votre recherche.
                  </p>
                )}

                {selectedAdherent && (
                  <div className="mt-2 p-3 bg-honey-50 border border-honey-200 rounded-lg flex items-center justify-between">
                    <div>
                      <div className="font-medium text-honey-950">
                        {selectedAdherent.prenom} {selectedAdherent.nom}
                      </div>
                      <div className="text-sm text-honey-800">{selectedAdherent.email}</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSelectedAdherent(null)}
                      className="text-honey-700 hover:text-honey-900"
                      aria-label="Retirer la sélection"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                )}

                {utilisateursEligibles.length === 0 && (
                  <p className="text-sm text-slate-500 mt-2">
                    Aucun utilisateur éligible. Tous les adhérents actifs sont déjà responsables.
                  </p>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  Un email de notification sera automatiquement envoyé au responsable nommé.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeAddModal}
                  className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={!selectedAdherent}
                  className="flex-1 px-4 py-2 bg-honey-600 text-white rounded-lg hover:bg-honey-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                >
                  Nommer
                </button>
              </div>
            </form>
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
}
